/**
 * useSigningSession — the "awaiting-signatures" listener the RSC
 * SessionDetail needs to bridge SignNow webhooks back into the cart pipeline.
 *
 * Legacy (SPFx): `ReviewScreen.tsx` set `isAwaitingSignatures=true` and
 * polled `SignNowClientService.getSessionStatus` every 2 s until all signers
 * completed, then called `resumePostSign(currentSessionMeta)`.
 *
 * v3: the same pattern but server-side. SignNow → `/signing/webhook` Function
 * writes a `signing.envelope.signed` / `signing.envelope.corrected` into the
 * SessionEvents table. This hook:
 *
 *   1. Polls `getSessionStatus` on a capped exponential back-off
 *      (5 s → 10 s → 20 s → 30 s max). Resets to 5 s on an observed status
 *      change.
 *   2. In parallel, polls `/v3/events` with `?since=<hookStartIso>` and
 *      `category=signing`. Dedupes on `eventId` so the same webhook never
 *      fires `onCompleted`/`onCorrected` twice (idempotent).
 *   3. Stops all polling on terminal status.
 *   4. Invokes `onCompleted(session)` once when the envelope reaches
 *      all-signed / completed — the caller typically funnels this into
 *      `PipelineService.resumePostSign` (see `apps/ROP-teams/src/services/
 *      PipelineService.ts`).
 *
 * Idempotence:
 *   The hook tracks two guards — `completedFiredRef` (envelope-signed) and a
 *   `seenEventIds` set for per-event dedupe. Parent components are safe to
 *   re-mount without risk of double-firing the post-sign resume.
 */

import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '@retail-ops/api-client';
import type {
  SessionEventDto,
  SigningSessionStatusResponse,
} from '@retail-ops/shared-types';
import { asIso8601 } from '@retail-ops/shared-types';

const INITIAL_POLL_MS = 5_000;
const MAX_POLL_MS = 30_000;
const EVENT_POLL_MS = 7_000;

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'all-signed',
  'completed',
  'complete',
  'declined',
  'expired',
  'cancelled',
  'voided',
  'error',
]);

const COMPLETED_STATUSES: ReadonlySet<string> = new Set([
  'all-signed',
  'completed',
  'complete',
]);

/** Webhook-relayed envelope event types we care about. */
const CORRECTION_EVENT_TYPES: ReadonlySet<string> = new Set([
  'signing.envelope.corrected',
  'signing-corrected',
  'signer-corrected',
]);

const COMPLETION_EVENT_TYPES: ReadonlySet<string> = new Set([
  'signing.envelope.signed',
  'signing.envelope.complete',
  'signing-complete',
  'envelope-signed',
]);

export interface UseSigningSessionOpts {
  /** Active envelope / session id for the polling target. */
  sessionId: string | undefined;
  /** Shared api-client (usually via `useApiClient()`). */
  apiClient: ApiClient;
  /** Fires once on first terminal-complete status or matching webhook event. */
  onCompleted?: (status: SigningSessionStatusResponse) => void;
  /**
   * Fires each time a correction webhook is relayed. Caller may refresh
   * correction history / bump an attempt counter.
   */
  onCorrected?: (event: SessionEventDto) => void;
  /** Pause polling without unmounting (e.g., panel collapsed). */
  paused?: boolean;
}

export interface UseSigningSessionState {
  status: string | undefined;
  loading: boolean;
  error: string | null;
  lastPolledAt: string | undefined;
  attempts: number;
}

export function useSigningSession(opts: UseSigningSessionOpts): UseSigningSessionState {
  const { sessionId, apiClient, onCompleted, onCorrected, paused } = opts;

  const [state, setState] = useState<UseSigningSessionState>({
    status: undefined,
    loading: false,
    error: null,
    lastPolledAt: undefined,
    attempts: 0,
  });

  // Timers + idempotency guards.
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentBackoffRef = useRef<number>(INITIAL_POLL_MS);
  const lastStatusRef = useRef<string | undefined>(undefined);
  const completedFiredRef = useRef<boolean>(false);
  const seenEventIdsRef = useRef<Set<string>>(new Set());
  const sinceRef = useRef<string>(asIso8601(new Date().toISOString()));
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!sessionId || paused) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    currentBackoffRef.current = INITIAL_POLL_MS;
    completedFiredRef.current = false;
    seenEventIdsRef.current = new Set<string>();
    sinceRef.current = asIso8601(new Date(Date.now() - 60_000).toISOString());

    const clearTimers = (): void => {
      if (statusTimerRef.current !== null) {
        clearTimeout(statusTimerRef.current);
        statusTimerRef.current = null;
      }
      if (eventTimerRef.current !== null) {
        clearTimeout(eventTimerRef.current);
        eventTimerRef.current = null;
      }
    };

    const pollStatus = async (): Promise<void> => {
      if (ctrl.signal.aborted) return;
      try {
        const resp = await apiClient.signing.getSessionStatus(sessionId, {
          signal: ctrl.signal,
        });
        if (ctrl.signal.aborted) return;
        const nextStatus = resp.Status;
        const prev = lastStatusRef.current;
        if (nextStatus && nextStatus !== prev) {
          // Status flipped — reset back-off to stay responsive.
          currentBackoffRef.current = INITIAL_POLL_MS;
          lastStatusRef.current = nextStatus;
        } else {
          // Unchanged — decay back-off.
          currentBackoffRef.current = Math.min(
            currentBackoffRef.current * 2,
            MAX_POLL_MS,
          );
        }
        setState((s) => ({
          ...s,
          status: nextStatus,
          loading: false,
          error: null,
          lastPolledAt: new Date().toISOString(),
          attempts: s.attempts + 1,
        }));
        if (nextStatus && COMPLETED_STATUSES.has(nextStatus) && !completedFiredRef.current) {
          completedFiredRef.current = true;
          onCompleted?.(resp);
        }
        if (nextStatus && TERMINAL_STATUSES.has(nextStatus)) {
          clearTimers();
          return;
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: err instanceof Error ? err.message : 'Poll failed.',
        }));
        // Keep polling through transient errors — legacy SigningPanel:496.
      }
      if (ctrl.signal.aborted) return;
      statusTimerRef.current = setTimeout(
        () => void pollStatus(),
        currentBackoffRef.current,
      );
    };

    const pollEvents = async (): Promise<void> => {
      if (ctrl.signal.aborted) return;
      try {
        const resp = await apiClient.retail.events.listSince(sinceRef.current, {
          category: 'signing',
          sessionId,
        });
        if (ctrl.signal.aborted) return;
        const events: ReadonlyArray<SessionEventDto> = resp.events ?? [];
        for (const ev of events) {
          if (seenEventIdsRef.current.has(ev.eventId)) continue;
          seenEventIdsRef.current.add(ev.eventId);
          // Advance the watermark to the newest event we've actually observed
          // so the next poll only returns strictly later ones.
          if (ev.timestamp > sinceRef.current) sinceRef.current = ev.timestamp;

          if (COMPLETION_EVENT_TYPES.has(ev.eventType) && !completedFiredRef.current) {
            completedFiredRef.current = true;
            // Re-fetch the authoritative status so the parent gets a real
            // SigningSessionStatusResponse payload, not a synthesized event.
            try {
              const st = await apiClient.signing.getSessionStatus(sessionId);
              onCompleted?.(st);
            } catch {
              // swallow — the next status poll will still fire onCompleted
              // via its own code path.
              completedFiredRef.current = false;
            }
          } else if (CORRECTION_EVENT_TYPES.has(ev.eventType)) {
            onCorrected?.(ev);
          }
        }
      } catch {
        // transient — keep polling.
      }
      if (ctrl.signal.aborted) return;
      eventTimerRef.current = setTimeout(() => void pollEvents(), EVENT_POLL_MS);
    };

    setState((s) => ({ ...s, loading: true, error: null }));
    void pollStatus();
    eventTimerRef.current = setTimeout(() => void pollEvents(), EVENT_POLL_MS);

    return () => {
      ctrl.abort();
      clearTimers();
    };
  }, [apiClient, sessionId, paused, onCompleted, onCorrected]);

  return state;
}
